import type { IHardwareCapacitySnapshot } from '@lucimark/shared/models/playback-heartbeat'

import { applyDevHardwareCapacityTierOverride } from '@/helpers/devHardwareCapacityTierOverride'

const WEB_CAPACITY_CACHE_TTL_MS = 5 * 60 * 1000
let webCapacityCache: { at: number, data: IHardwareCapacitySnapshot } | null = null

function getWebGLVersion(): string {
  if (typeof document === 'undefined') { return 'web' }
  try {
    const canvas = document.createElement('canvas')
    if (canvas.getContext('webgl2')) { return 'WebGL 2.0' }
    if (canvas.getContext('webgl')) { return 'WebGL 1.0' }
    return 'web'
  }
  catch {
    return 'web'
  }
}

/**
 * Builds a hardware capacity snapshot from browser APIs (navigator.hardwareConcurrency,
 * navigator.deviceMemory, WebGL). Used when platform is web so heartbeats include
 * hardwareCapacity and the app can show "Device capacity" for web players.
 * Returns null when not in a browser or APIs are missing.
 */
export function getWebHardwareCapacity(): IHardwareCapacitySnapshot | null {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') { return null }

  if (webCapacityCache != null && Date.now() - webCapacityCache.at < WEB_CAPACITY_CACHE_TTL_MS) {
    return applyDevHardwareCapacityTierOverride(webCapacityCache.data)
  }

  const cpuCores = navigator.hardwareConcurrency ?? 0
  const deviceMemoryGb = (navigator as { deviceMemory?: number }).deviceMemory
  const totalRamMb = deviceMemoryGb != null ? Math.round(deviceMemoryGb * 1024) : 0

  let tier: string
  if (deviceMemoryGb != null && cpuCores > 0) {
    if (deviceMemoryGb >= 4 && cpuCores >= 4) { tier = 'high' }
    else if (deviceMemoryGb >= 2 || cpuCores >= 2) { tier = 'mid' }
    else { tier = 'low' }
  }
  else {
    tier = 'web'
  }

  const data: IHardwareCapacitySnapshot = {
    tier,
    totalRamMb,
    availRamMb: 0,
    cpuCores,
    maxCpuFreqMhz: -1,
    glEsVersion: getWebGLVersion(),
    isLowMemory: false,
    androidApiLevel: 0,
    abi: 'web'
  }

  webCapacityCache = { at: Date.now(), data }
  return applyDevHardwareCapacityTierOverride(data)
}
